/* ap-lead-capture.js — site-wide safety net for customer enquiries.
 *
 * 2026-09-09, re-deployed 2026-09-13 in theme v135 (newsletter guard; beacon copy, never blocks the submit). Background: every lead form on arnoldprints.com posted to a place
 * we did not control and kept no copy of our own.
 *   - contact-us + business-quote-form  -> Formspree, which silently spam-files
 *     submissions and never emails those (2026-09-07 digest: 9 in, 6 flagged spam)
 *   - pages/design + wholesale-apply    -> Shopify's contact form, email only,
 *     and not one of those has ever turned up in the mail hub
 *   - pages/wholesale                   -> /apps/wholesale/apply, which returns 404
 * Real work was lost this way (the Big Dog Coatings vinyl enquiry; Russell Keller
 * on 2026-08-25 saying he uploaded a file and never heard back).
 *
 * This script is purely ADDITIVE. It never blocks, delays or replaces a form's
 * own submission: it sends a copy to our worker alongside it. Worker down, slow,
 * or erroring, the customer's submit still goes through exactly as before. It also picks up any lead form added to
 * the site later, including ones built in PageFly that we cannot edit directly.
 */
(function () {
  'use strict';

  var LEAD_ENDPOINT = 'https://ap-approvals.sales-b43.workers.dev/api/lead';
  var MIRROR_TIMEOUT_MS = 4000;   // never make a customer wait longer than this
  var MAX_MIRROR_BYTES  = 60 * 1024 * 1024;
  // sendBeacon / keepalive requests share a ~64KB browser quota, and the OpenAI
  // pixel uses the same pool. Stay well under it for copies of navigating forms.
  var BEACON_MAX_BYTES  = 48 * 1024;

  // Forms that submit themselves with their own fetch() and carry no action
  // attribute, so the rules below cannot see them. business-quote-form is the
  // big one: 11 file inputs whose artwork was previously never uploaded at all,
  // only named. For these we copy the submission and then keep our hands off,
  // letting the page's own handler run exactly as it always has.
  //   intakeForm -> business-quote-form (11 artwork inputs, never uploaded before)
  //   apwForm    -> pages/wholesale. Its own POST to /apps/wholesale/apply returns
  //                 404 right now, so every partner application, W-9 and resale
  //                 certificate is being lost outright. Mirroring rescues them.
  var MIRROR_ONLY_IDS = ['intakeForm', 'apwForm'];

  // Only forms that actually carry a customer enquiry. Deliberately excludes
  // search, cart, login/register, and newsletter signups.
  function mirrorOnly(form) {
    return !!form && MIRROR_ONLY_IDS.indexOf(form.id) !== -1;
  }

  function isLeadForm(form) {
    if (!form) return false;
    if (mirrorOnly(form)) return true;
    var action = (form.getAttribute('action') || '').toLowerCase();
    if (action.indexOf('formspree.io') !== -1) return true;
    if (action.indexOf(LEAD_ENDPOINT) !== -1) return false;   // already ours
    if (action.indexOf('/contact') !== -1) {
      // Newsletter signups ALSO post to /contact with a contact[email] field, but
      // Shopify marks them form_type=customer (the /pages/design signup is one).
      // A real enquiry is form_type=contact, so rule signups out before anything else.
      var formType = form.querySelector('input[name="form_type"]');
      if (formType && formType.value === 'customer') return false;
      if (form.querySelector('input[name="contact[tags]"][value*="newsletter" i]')) return false;
      // Shopify's contact form. Require a real email field so we skip footer
      // newsletter forms that also post to /contact.
      if (form.querySelector('[name="contact[email]"]')) return true;
      var t = (form.getAttribute('id') || '') + ' ' + (form.className || '');
      if (/newsletter|subscribe|footer/i.test(t)) return false;
      return !!form.querySelector('input[type=email]');
    }
    return false;
  }

  function sourcePage() {
    var p = location.pathname.replace(/\/+$/, '');
    var seg = p.split('/').filter(Boolean);
    return seg.length ? seg[seg.length - 1] : 'home';
  }

  function buildPayload(form) {
    var fd;
    try { fd = new FormData(form); } catch (e) { fd = new FormData(); }
    // Shopify names its fields contact[name] etc. Add plain aliases so the
    // worker's field matching finds them without special-casing every theme.
    var alias = { 'contact[name]': 'name', 'contact[email]': 'email',
                  'contact[phone]': 'phone', 'contact[body]': 'message',
                  'contact[company]': 'company' };
    Object.keys(alias).forEach(function (k) {
      var v = fd.get(k);
      if (v && !fd.get(alias[k])) fd.append(alias[k], v);
    });
    // FormData ignores file inputs with no name attribute, which is exactly how
    // the wholesale application's document upload is written. Add them by id so
    // W-9s and resale certificates are not silently dropped.
    var fileInputs = form.querySelectorAll('input[type=file]:not([name])');
    for (var i = 0; i < fileInputs.length; i++) {
      var fl = fileInputs[i].files || [];
      for (var j = 0; j < fl.length; j++) {
        fd.append('file_' + (fileInputs[i].id || i) + '_' + j, fl[j], fl[j].name);
      }
    }
    fd.append('Source page', sourcePage());
    fd.append('Captured by', 'ap-lead-capture (site-wide)');
    fd.append('Page URL', location.href);
    fd.append('Submitted', new Date().toString());
    return fd;
  }

  function tooBig(fd) {
    var total = 0;
    try {
      fd.forEach(function (v) { if (v && typeof v.size === 'number') total += v.size; });
    } catch (e) { return false; }
    return total > MAX_MIRROR_BYTES;
  }

  function mirror(form) {
    var fd = buildPayload(form);
    if (tooBig(fd)) {
      // Too large to copy. Send the text fields alone so we still get the lead
      // and know to chase the artwork.
      var lite = new FormData();
      fd.forEach(function (v, k) { if (!(v && typeof v.size === 'number')) lite.append(k, v); });
      lite.append('ACTION NEEDED', 'Customer attached files too large to copy. Ask them to email the artwork.');
      fd = lite;
    }
    var ctl = new AbortController();
    var timer = setTimeout(function () { ctl.abort(); }, MIRROR_TIMEOUT_MS);
    return fetch(LEAD_ENDPOINT, {
      method: 'POST', body: fd, mode: 'cors',
      headers: { 'Accept': 'application/json' },
      signal: ctl.signal
    }).then(function (r) { clearTimeout(timer); return r; })
      .catch(function () { clearTimeout(timer); });   // never rethrow
  }

  // Copy for forms that NAVIGATE on submit (Formspree, Shopify /contact, PageFly).
  // We never hold or re-fire these: Shopify's contact-form bot protection cancels
  // any submit event a script dispatches (only a real click counts), so the
  // 2026-09-09 hold-and-resubmit approach left Shopify contact forms never
  // submitting at all. Instead the customer's own submit runs untouched and this
  // copy rides a beacon, which the browser finishes sending after the page leaves.
  function beaconMirror(form) {
    var fd = buildPayload(form), size = 0, hasFiles = false;
    try {
      fd.forEach(function (v, k) {
        if (v && typeof v.size === 'number') { size += v.size; hasFiles = true; }
        else { size += String(k).length + String(v).length + 64; }
      });
    } catch (e) {}
    if (size > BEACON_MAX_BYTES) {
      var lite = new FormData();
      fd.forEach(function (v, k) { if (!(v && typeof v.size === 'number')) lite.append(k, String(v).slice(0, 4000)); });
      if (hasFiles) lite.append('ACTION NEEDED', 'Customer attached files that were too large to copy here. Ask the customer to email them.');
      fd = lite;
    }
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(LEAD_ENDPOINT, fd)) return;
    } catch (e) {}
    try {
      fetch(LEAD_ENDPOINT, { method: 'POST', body: fd, mode: 'cors', keepalive: true })
        .catch(function () {});
    } catch (e) {}
  }

  // One listener on the document in the CAPTURE phase, so it sees every submit
  // before a page's own handler can cancel it. It NEVER calls preventDefault or
  // stops propagation: the customer's submission always runs exactly as it would
  // without this script.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!isLeadForm(form)) return;
    if (!form.noValidate && form.checkValidity && !form.checkValidity()) return;
    var now = Date.now(), last = +(form.dataset.apLeadAt || 0);
    if (now - last < 3000) return;          // double-click guard
    form.dataset.apLeadAt = String(now);

    // JS-driven form (no navigation): full copy including artwork files.
    if (mirrorOnly(form)) { mirror(form); return; }

    // Navigating form: beacon copy, original submit untouched.
    beaconMirror(form);
  }, true);

})();